<template>
  <v-card flat>
    <v-container class="px-6 pt-0">
      <v-row no-gutters>
        <v-col cols="12" v-if="access && showAddForm !== ''">
          <range-user-form
            :show-add-user-range-form="showAddForm"
            :role-options="roleOptions"
            :group-uuid="groupUuid"
            @update:loading="handleLoading"
            @update:roles="group.roles = $event"
            @update:ranges="$emit('reload', $event)"
          ></range-user-form>
        </v-col>
        <template v-if="loading">
          <v-col cols="12">
            <v-row justify="center">
              <v-progress-circular
                :size="55"
                :width="4"
                color="primary"
                indeterminate
              ></v-progress-circular>
            </v-row>
          </v-col>
        </template>
        <template v-else>
          <v-col cols="12" v-if="group.roles.length !== 0">
            <v-card-text class="ml-2" v-if="showUsersSubtitle(group.roles)">
              {{ $t("components.miscellaneous.groupDetails.users") }}
            </v-card-text>
            <v-card-text class="ml-2" v-else>
              {{ $t("components.miscellaneous.groupDetails.noUsersAvailable") }}
            </v-card-text>
            <div v-for="(role, index) in group.roles" :key="index">
              <v-list v-for="(user, ind) in role.users" :key="ind">
                <user-email-component
                  :user="user"
                  :readonly="!access"
                  @delete-user="deleteUserDialog(user, role, true)"
                  ><template v-slot:role>
                    <v-select
                      class="mt-0 pt-0"
                      v-model="role.uuid"
                      :items="roleOptions"
                      color="primary"
                      item-text="name"
                      item-value="uuid"
                      @change="inviteUser(user, role)"
                      :disabled="!access"
                    ></v-select>
                  </template>
                </user-email-component>
              </v-list>
            </div>
          </v-col>
          <v-col cols="12" v-if="group.roles.length !== 0">
            <div v-for="(role, index) in group.roles" :key="index">
              <v-list v-for="(user, ind) in role.pending_invites" :key="ind">
                <!-- HACK :contact-email="'delete'"  -->
                <user-email-component
                  :user="user"
                  :readonly="!access"
                  :invite="true"
                  :contact-email="'delete'"
                  @delete-user="deleteUserDialog(user, role, false)"
                  ><template v-slot:role>
                    <v-select
                      class="mt-0 pt-0"
                      v-model="role.uuid"
                      :items="roleOptions"
                      color="primary"
                      item-text="name"
                      item-value="uuid"
                      :disabled="true"
                      @change="inviteUser(user, role)"
                    ></v-select>
                  </template>
                  <template v-slot:pending>
                    <v-chip class="mt-2">{{
                      $t("components.miscellaneous.groupDetails.pending")
                    }}</v-chip>
                  </template>
                </user-email-component>
              </v-list>
            </div>
          </v-col>
        </template>
      </v-row>
    </v-container>

    <warning-dialog
      :dialog="cancelDialog.active"
      :text="cancelDialog.text"
      :title="cancelDialog.title"
      @agree="handleCancelDialogOutput"
      :confirm="$t('components.miscellaneous.groupDetails.yes')"
      :reject="$t('components.miscellaneous.groupDetails.no')"
      v-show="false"
    />
  </v-card>
</template>

<script>
import WarningDialog from "@/components/dialogs/WarningDialog.vue";
import api from "@/utils/api.js";
import snackbar from "@/utils/snackbar.js";
import UserEmailComponent from "@/components/miscellaneous/UserEmailComponent";
import RangeUserForm from "@/components/miscellaneous/RangeUserForm";
import { USER_CRM_REQUEST } from "@/store/actions/user";

export default {
  components: {
    UserEmailComponent,
    WarningDialog,
    RangeUserForm,
  },
  props: {
    access: {
      type: Boolean,
    },
    groupUuid: {
      type: String,
      default: "",
    },
    showAddUserRangeForm: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    group: {},
    loading: true,
    userToDelete: {},
    deleteDialog: false,
    valid: false,
    emailRules: [],
    cancelDialog: {},
    roleOptions: [],
  }),

  computed: {
    showAddForm() {
      return this.showAddUserRangeForm;
    },
  },

  created() {
    this.setInitialData();
    this.getGroupDetails();
  },
  methods: {
    setInitialData() {
      this.emailRules = [
        (v) =>
          /[^@]+@[^.]+\..*/.test(v) ||
          this.$t("components.miscellaneous.groupDetails.eMailMustBeValid"),
      ];
      this.cancelDialog = {
        active: false,
        title: this.$t("components.miscellaneous.groupDetails.removeUser"),
        text: this.$t(
          "components.miscellaneous.groupDetails.areYouSureYouWantToRemoveUser"
        ),
      };
    },
    handleLoading(event) {
      this.$emit("update:loading", event);
    },
    showUsersSubtitle(roles) {
      const users = roles.flatMap((role) => {
        return role.users;
      });
      const pendingUsers = roles.flatMap((role) => {
        return role.pending_invites;
      });

      return [...users, ...pendingUsers].length !== 0;
    },
    getGroupDetails() {
      this.loading = true;
      this.$emit("update:loading", true);
      api.getGroup(this.groupUuid).then((resp) => {
        this.group = resp.data;
        this.roleOptions = this.group.roles.map((item) => {
          return { name: item.name, uuid: item.uuid };
        });
        this.$emit("update:loading", false);
        this.loading = false;
      });
    },
    inviteUser(user, role) {
      let username = typeof user !== "undefined" ? user.username : this.email;
      let roleUuid = typeof role !== "undefined" ? role.uuid : this.role;

      if (typeof user !== "undefined" && typeof role !== "undefined") {
        this.setUser(roleUuid, username);
      } else if (this.$refs.form.validate()) {
        this.setUser(roleUuid, username);
      } else {
        snackbar.error();
      }
    },

    setUser(roleUuid, username) {
      this.loading = true;
      this.$emit("update:loading", true);
      api
        .inviteRoleUser(this.group.uuid, roleUuid, {
          email: username,
          replace_existing: true,
        })
        .then((resp) => {
          api.getGroup(this.group.uuid).then((resp) => {
            this.group.roles = resp.data.roles;
            snackbar.success(
              this.$t("utils.snackbar.success.userSuccessfullyInvited")
            );
            this.$store.dispatch(USER_CRM_REQUEST).then((resp) => {
              this.loading = false;
              this.$emit("update:loading", false);
            });
          });
        })
        .catch((error) => {
          if (
            typeof error.response.data.error_type !== "undefined" &&
            error.response.data.error_type === "MEMBER_OF_OTHER_SUPERGROUPS"
          ) {
            snackbar.error(error.response.data.error);
          } else {
            snackbar.error();
          }
          this.loading = false;
          this.$emit("update:loading", false);
        });
    },

    deleteUserDialog(user, role, status) {
      this.userToDelete = { user: user, role: role.uuid, status: status };
      this.cancelDialog.active = true;
    },
    handleCancelDialogOutput(agreed) {
      this.loading = false;
      if (agreed) {
        if (this.userToDelete.status) {
          api
            .deleteUserFromRole(this.group.uuid, this.userToDelete.role, {
              uuid: this.userToDelete.user.uuid,
            })
            .then((resp) => {
              this.group.roles
                .find((element) => {
                  return element.uuid === this.userToDelete.role;
                })
                .users.forEach((user, index, arr) => {
                  if (user.uuid === this.userToDelete.user.uuid) {
                    arr.splice(index, 1);
                  }
                });

              this.$store.dispatch(USER_CRM_REQUEST);

              this.userToDelete = null;
              this.cancelDialog.active = false;
              snackbar.success(
                this.$t("utils.snackbar.success.userSuccessfullyRemoved")
              );
            });
        } else {
          api
            .rejectInviteRoleUser(this.userToDelete.user.token)
            .then((resp) => {
              this.group.roles
                .find((element) => {
                  return element.uuid === this.userToDelete.role;
                })
                .pending_invites.forEach((user, index, arr) => {
                  if (user.uuid === this.userToDelete.user.uuid) {
                    arr.splice(index, 1);
                  }
                });

              this.userToDelete = null;
              this.cancelDialog.active = false;
              snackbar.success(
                this.$t("utils.snackbar.success.userSuccessfullyRemoved")
              );
            });
        }
      } else {
        this.cancelDialog.active = false;
        this.userToDelete = null;
      }
    },
  },
};
</script>
